import type { TriggerProgressContext } from '../../../virtual-appointments/virtualAppointmentDraft';
import type { AlignerProgress } from '../../../virtual-appointments/virtualAppointmentTriggerBaseline';

// 'next': the VAs fire on the series a refinement delivers, where aligners and weeks restart.
export type PlannedTriggerSeries = 'current' | 'next';

export interface PlannerPatientFacts {
    inTreatment: boolean;
    /** UTC day the current Treatment phase began; null when none is on record. */
    treatmentStartDate: string | null;
    currentAligner: number | null;
    totalAligners: number | null;
    dmLinked: boolean;
}

export interface PlannerTriggerScope {
    treatmentStartDate: string | null;
    dmLinked: boolean;
    /** Shown under an aligner trigger only; it never caps one. */
    aligners: AlignerProgress;
    /** In Treatment with no start on record: a week can only count from now. */
    weeksUnavailable: boolean;
}

export interface PlannerScopeInput {
    series: PlannedTriggerSeries;
    /** Null while the patient card is loading or failed. */
    facts: PlannerPatientFacts | null;
}

// The next series' weeks restart with it, so they can't count from now.
export function offersWeeksFromNow(series: PlannedTriggerSeries): boolean {
    return series !== 'next';
}

// Null keeps the context-free checks: a restarting series, outside Treatment, or the patient not loaded.
export function plannerTriggerScope({
    series,
    facts
}: PlannerScopeInput): PlannerTriggerScope | null {
    if (series === 'next' || facts === null || !facts.inTreatment) return null;
    return {
        treatmentStartDate: facts.treatmentStartDate,
        dmLinked: facts.dmLinked,
        aligners: { current: facts.currentAligner, total: facts.totalAligners },
        weeksUnavailable: facts.treatmentStartDate === null
    };
}

export type PlannerTriggerProgress = Required<
    Pick<TriggerProgressContext, 'currentAligner' | 'alignerLimit' | 'weeksUnavailable'>
>;

// A wizard plans aligners for a series whose count nobody knows yet, so only the range applies.
export function plannerTriggerProgress(scope: PlannerTriggerScope): PlannerTriggerProgress {
    return { currentAligner: null, alignerLimit: null, weeksUnavailable: scope.weeksUnavailable };
}
